/**
 * Thumbnail Cache Utility
 * Module-level cache to persist thumbnails across component renders.
 * Uses a Map with composite key (documentId:pageNumber) to store blob URLs.
 * Implements LRU-style eviction when cache exceeds MAX_CACHE_SIZE.
 */

const thumbnailCache = new Map();
const MAX_CACHE_SIZE = 200; // Maximum number of cached thumbnails

/**
 * Get cache key for a page
 */
export const getCacheKey = (documentId, pageNumber) =>
  `${documentId}:${pageNumber}`;

/**
 * Get cached thumbnail URL if available
 */
export const getCachedThumbnail = (documentId, pageNumber) => {
  const key = getCacheKey(documentId, pageNumber);
  return thumbnailCache.get(key);
};

/**
 * Store thumbnail URL in cache with LRU eviction
 */
export const cacheThumbnail = (documentId, pageNumber, url) => {
  const key = getCacheKey(documentId, pageNumber);

  // If cache is full, remove oldest entry (first item in Map)
  if (thumbnailCache.size >= MAX_CACHE_SIZE) {
    const oldestKey = thumbnailCache.keys().next().value;
    const oldUrl = thumbnailCache.get(oldestKey);
    if (oldUrl) {
      URL.revokeObjectURL(oldUrl);
    }
    thumbnailCache.delete(oldestKey);
  }

  thumbnailCache.set(key, url);
};

/**
 * Clear all cached thumbnails - useful when changing projects
 * Revokes all blob URLs to free memory
 */
export const clearThumbnailCache = () => {
  thumbnailCache.forEach((url) => {
    URL.revokeObjectURL(url);
  });
  thumbnailCache.clear();
};

/**
 * Expose the underlying map for specific operations (like retrying a single failed thumbnail)
 */
export const removeThumbnailFromCache = (documentId, pageNumber) => {
  const key = getCacheKey(documentId, pageNumber);
  const cachedUrl = thumbnailCache.get(key);
  if (cachedUrl) {
    URL.revokeObjectURL(cachedUrl);
    thumbnailCache.delete(key);
  }
};
